import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaRupeeSign,
  FaUsers,
  FaExternalLinkAlt,
  FaTimes,
} from "react-icons/fa";
import { FaMapMarkedAlt, FaClipboardList, FaChartPie } from "react-icons/fa";
import { MdLocationOn, MdAttachMoney } from "react-icons/md";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Close } from "@mui/icons-material";

const jharkhandDistricts = [
  { name: "Ranchi", schemes: 25 },
  { name: "Giridih", schemes: 18 },
  { name: "Dhanbad", schemes: 22 },
  { name: "Bokaro", schemes: 15 },
  { name: "Jamshedpur", schemes: 20 },
  { name: "Hazaribagh", schemes: 12 },
  { name: "Deoghar", schemes: 14 },
  { name: "Palamu", schemes: 10 },
  { name: "Chatra", schemes: 8 },
  { name: "Koderma", schemes: 9 },
  { name: "Simdega", schemes: 7 },
  { name: "Latehar", schemes: 6 },
  { name: "Gumla", schemes: 11 },
  { name: "Lohardaga", schemes: 5 },
  { name: "Seraikela", schemes: 13 },
  { name: "Khunti", schemes: 8 },
  { name: "Ramgarh", schemes: 16 },
  { name: "West Singhbhum", schemes: 19 },
  { name: "East Singhbhum", schemes: 21 },
  { name: "Sahibganj", schemes: 10 },
  { name: "Pakur", schemes: 7 },
  { name: "Godda", schemes: 9 },
  { name: "Dumka", schemes: 11 },
  { name: "Jamtara", schemes: 6 },
];

export default function Report() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [districtData, setDistrictData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({});

  const fetchSchemes = async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        pageNo: 1,
        pageSize: 10,
      };

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(data?.data || []);
      setSchemeStats({
        totalSchemes: data?.totalSchemes,
        totalDraftSchemes: data?.totalDraftSchemes,
        totalCompletedSchemes: data?.totalCompletedSchemes,
        totalAllocationAmount: data?.totalAllocationAmount,
      });
    } catch (error) {
      console.error("Error fetching schemes:", error);
      toast.error("Failed to fetch schemes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, []);

  // Calculate total allocated amount for each district
  const getDistrictAllocatedAmount = (districtName) => {
    const districtSchemes = schemes.filter(
      (item) =>
        item.district &&
        item.district.toLowerCase().includes(districtName.toLowerCase())
    );
    return districtSchemes.reduce(
      (sum, item) => sum + (item.allocatedAmount || 0),
      0
    );
  };

  const getDistrictSchemesCount = (districtName) => {
    return schemes.filter(
      (item) =>
        item.district &&
        item.district.toLowerCase().includes(districtName.toLowerCase())
    ).length;
  };

  const handleDistrictClick = async (district) => {
    setLoading(true);
    setSelectedDistrict(district);

    // Filter data for selected district
    const filteredData = schemes.filter(
      (item) =>
        item.district &&
        item.district.toLowerCase().includes(district.name.toLowerCase())
    );

    setDistrictData(filteredData);
    setTimeout(() => {
      setLoading(false);
      setModalOpen(true);
    }, 300);
  };

  const filteredDistricts = jharkhandDistricts.filter((district) =>
    district.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <motion.div
      className="min-h-screen p-6"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Jharkhand District Reports
        </h1>
        <p className="text-gray-600">
          Overview of schemes and allocated funds across all districts of
          Jharkhand
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search districts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          {/* <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {["All", "High Budget", "Most Schemes", "Low Budget"].map(
              (status) => (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                    selectedStatus === status
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  {status}
                </button>
              )
            )}
          </div> */}
        </div>

        {/* Stats Summary */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Districts */}
          <div className="bg-blue-600 p-4 rounded-xl border border-blue-700 text-white shadow-sm flex items-center gap-4">
            <div className="p-3 bg-blue-700/40 rounded-lg">
              <FaMapMarkedAlt className="text-2xl" />
            </div>
            <div>
              <div className="text-sm font-semibold">Total Districts</div>
              <div className="text-2xl font-bold">
                {jharkhandDistricts.length}
              </div>
            </div>
          </div>

          {/* Total Schemes */}
          <div className="bg-green-600 p-4 rounded-xl border border-green-700 text-white shadow-sm flex items-center gap-4">
            <div className="p-3 bg-green-700/40 rounded-lg">
              <FaClipboardList className="text-2xl" />
            </div>
            <div>
              <div className="text-sm font-semibold">Total Schemes</div>
              <div className="text-2xl font-bold">
                {jharkhandDistricts.reduce((sum, d) => sum + d.schemes, 0)}
              </div>
            </div>
          </div>

          {/* Total Allocated */}
          <div className="bg-orange-600 p-4 rounded-xl border border-orange-700 text-white shadow-sm flex items-center gap-4">
            <div className="p-3 bg-orange-700/40 rounded-lg">
              <FaRupeeSign className="text-2xl" />
            </div>
            <div>
              <div className="text-sm font-semibold">Total Allocated</div>
              <div className="text-2xl font-bold">
                ₹
                {(
                  jharkhandDistricts.reduce(
                    (sum, d) => sum + getDistrictAllocatedAmount(d.name),
                    0
                  ) / 10000000
                ).toFixed(2)}{" "}
                Cr
              </div>
            </div>
          </div>

          {/* Avg. per District */}
          <div className="bg-purple-600 p-4 rounded-xl border border-purple-700 text-white shadow-sm flex items-center gap-4">
            <div className="p-3 bg-purple-700/40 rounded-lg">
              <FaChartPie className="text-2xl" />
            </div>
            <div>
              <div className="text-sm">Avg. per District</div>
              <div className="text-2xl font-bold">
                ₹
                {(
                  jharkhandDistricts.reduce(
                    (sum, d) => sum + getDistrictAllocatedAmount(d.name),
                    0
                  ) /
                  jharkhandDistricts.length /
                  100000
                ).toFixed(2)}{" "}
                L
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* District Cards Grid */}
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        variants={containerVariants}
      >
        <AnimatePresence>
          {filteredDistricts.map((district) => {
            const allocatedAmount = getDistrictAllocatedAmount(district.name);
            const schemeCount = getDistrictSchemesCount(district.name);

            return (
              <motion.div
                key={district.name}
                variants={itemVariants}
                layout
                whileTap={{ scale: 0.95 }}
                onClick={() => handleDistrictClick(district)}
                className="relative hover:bg-blue-50 duration-500 bg-white border border-gray-200 rounded-xl shadow-sm 
                   hover:shadow-md cursor-pointer transition-all p-4"
              >
                {/* Header */}
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {district.name}
                    </h3>
                    <p className="text-xs text-gray-500">District, Jharkhand</p>
                  </div>

                  <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <MdLocationOn className="text-blue-600 text-lg" />
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Allocated Amount</span>
                    <span className="font-semibold text-gray-800">
                      ₹{(allocatedAmount / 100000).toFixed(2)} L
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Schemes</span>
                    <span className="font-medium text-gray-800">
                      {schemeCount}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg. per Scheme</span>
                    <span className="font-medium text-gray-800">
                      ₹
                      {(allocatedAmount / (schemeCount || 1) / 100000).toFixed(
                        2
                      )}{" "}
                      L
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-xs text-gray-500">
                  <span>Click to view details</span>
                  <FaExternalLinkAlt className="text-gray-500" />
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {/* No Results */}
      {filteredDistricts.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16"
        >
          <div className="text-gray-400 text-6xl mb-4">🏙️</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No districts found
          </h3>
          <p className="text-gray-500">
            Try adjusting your search to find what you're looking for.
          </p>
        </motion.div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl">
            <CircularProgress className="text-blue-600" />
            <p className="mt-4 text-gray-700">Loading district data...</p>
          </div>
        </div>
      )}

      {/* District Detail Modal */}
      <DistrictDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        districtData={districtData}
        selectedDistrict={selectedDistrict}
      />
    </motion.div>
  );
}

// District Detail Modal Component
function DistrictDetailModal({
  open,
  onClose,
  districtData,
  selectedDistrict,
}) {
  const navigate = useNavigate();
  if (!districtData || !selectedDistrict) return null;

  // Calculate total allocated amount for the district
  const totalAllocated = districtData.reduce(
    (sum, item) => sum + (item.allocatedAmount || 0),
    0
  );
  const totalSchemes = districtData.length;
  const completedSchemes = districtData.filter(
    (item) => item.schemeStatus === "Complete"
  ).length;
  const inProgressSchemes = districtData.filter(
    (item) => item.schemeStatus === "In Progress"
  ).length;
  const planningSchemes = districtData.filter(
    (item) => item.schemeStatus === "Planning"
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle className="bg-gradient-to-r from-blue-50 to-blue-100 border-b">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {selectedDistrict.name} District
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              Scheme Details & Statistics
            </p>
          </div>

          <div className="flex items-center gap-6">
            <button
              className="px-4 py-2 text-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:bg-none disabled:text-gray-600 bg-blue-200 border-blue-300 text-base rounded-lg hover:bg-blue-300 duration-300"
              onClick={() =>
                navigate("../reportSchemes", {
                  state: {
                    district: districtData[0]?.district,
                  },
                })
              }
              disabled={districtData?.length === 0}
            >
              View Schemes
            </button>

            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="p-6">
        {/* District Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
            <div className="text-sm text-blue-600 font-medium">
              Total Allocated
            </div>
            <div className="text-2xl font-bold text-gray-800 mt-1">
              ₹{(totalAllocated / 100000).toFixed(2)} L
            </div>
          </div>
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
            <div className="text-sm text-emerald-600 font-medium">
              Total Schemes
            </div>
            <div className="text-2xl font-bold text-gray-800 mt-1">
              {totalSchemes}
            </div>
          </div>
          <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
            <div className="text-sm text-amber-600 font-medium">
              In Progress
            </div>
            <div className="text-2xl font-bold text-gray-800 mt-1">
              {inProgressSchemes}
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
            <div className="text-sm text-purple-600 font-medium">Completed</div>
            <div className="text-2xl font-bold text-gray-800 mt-1">
              {completedSchemes}
            </div>
          </div>
        </div>

        {/* Important Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-700">
              Financial Information
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Financial Year</span>
                <span className="font-medium">
                  {districtData[0]?.financialYear || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Payment Status</span>
                <Chip
                  label={districtData[0]?.paymentStatus || "N/A"}
                  size="small"
                  className={`${
                    districtData[0]?.paymentStatus === "Complete"
                      ? "bg-green-100 text-green-800"
                      : districtData[0]?.paymentStatus === "Partial"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                />
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Tender Status</span>
                <span className="font-medium">
                  {districtData[0]?.tenderStatus || "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-gray-700">Project Details</h4>
            <div className="space-y-2">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Sector</span>
                <span className="font-medium">
                  {districtData[0]?.sector || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">DDC/CEO</span>
                <span className="font-medium">
                  {districtData[0]?.ddcCumCEOName || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">DDO Code</span>
                <span className="font-medium">
                  {districtData[0]?.ddoCode || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scheme List */}
        {/* <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Schemes in {selectedDistrict.name}
          </h3>
          <div className="space-y-3">
            {districtData.slice(0, 5).map((scheme) => (
              <div
                key={scheme.id}
                className="p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-800">
                      {scheme.schemeName || scheme.proposalName}
                    </h4>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <MdAttachMoney className="text-emerald-500" />₹
                        {(scheme.allocatedAmount || 0).toLocaleString("en-IN")}
                      </span>
                      <span className="flex items-center gap-1">
                        <MdLocationOn className="text-blue-500" />
                        {scheme.block} Block
                      </span>
                      <span className="flex items-center gap-1">
                        <FaUsers className="text-purple-500" />
                        {scheme.numberOfBeneficiaries || "N/A"} beneficiaries
                      </span>
                    </div>
                  </div>
                  <Chip
                    label={scheme.schemeStatus || "N/A"}
                    size="small"
                    className={`${
                      scheme.schemeStatus === "Complete"
                        ? "bg-green-100 text-green-800"
                        : scheme.schemeStatus === "In Progress"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  />
                </div>
                {scheme.progressPercentage !== null && (
                  <div className="mt-3">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{scheme.progressPercentage}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          scheme.progressPercentage === 100
                            ? "bg-green-500"
                            : "bg-blue-500"
                        } rounded-full`}
                        style={{ width: `${scheme.progressPercentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div> */}
      </DialogContent>
    </Dialog>
  );
}
